import {useCallback, useMemo} from 'react';
import {v4 as uuidv4} from 'uuid';
import {
    ResourceAllocationState,
    ResourceProjectAssignment,
    Allocation
} from '../../../types';
import {signalRService as signalRServiceInstance} from '../../../services/signalr';

// One cell write in a bulk edit; omit id to create (a fresh uuid is minted).
export type AllocationEditUpsert = Omit<Allocation, 'id'> & {id?: string};

export interface AllocationEditBatch {
    upserts: AllocationEditUpsert[];
    deleteIds?: string[];
}

interface ActionDeps {
    // Ref, not the state value: callbacks read state via stateRef.current so
    // their identities survive state changes — the provider value can then be
    // memoized and downstream memo boundaries hold.
    stateRef: {current: ResourceAllocationState};
    setState: React.Dispatch<React.SetStateAction<ResourceAllocationState>>;
    authenticatedFetch: (
        url: string,
        options?: RequestInit
    ) => Promise<Response>;
    signalRService: typeof signalRServiceInstance;
    customerId: string | undefined;
    baseUrl: string;
    refetchState: () => Promise<void>;
}

export const useAllocationActions = ({
    stateRef,
    setState,
    authenticatedFetch,
    signalRService,
    customerId,
    baseUrl,
    refetchState
}: ActionDeps) => {
    // Assignment operations
    const addAssignment = useCallback(
        async (
            assignment: Omit<ResourceProjectAssignment, 'id' | 'createdAt'>
        ): Promise<string> => {
            const id = uuidv4();
            const createdAt = new Date();
            const newAssignment = {...assignment, id, createdAt};

            // Optimistic update
            setState(prev => ({
                ...prev,
                assignments: {
                    ...prev.assignments,
                    [id]: newAssignment
                }
            }));

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                try {
                    const response = await authenticatedFetch(
                        `${baseUrl}/api/resource-allocation/assignments`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-SignalR-Connection-Id': connectionId || ''
                            },
                            body: JSON.stringify({
                                id: id,
                                projectId: assignment.projectId,
                                targetType: assignment.targetType,
                                personId: assignment.personId,
                                teamId: assignment.teamId,
                                jobRoleId: assignment.jobRoleId,
                                allocationType: assignment.allocationType,
                                involvement: assignment.involvement,
                                label: assignment.label
                            })
                        }
                    );

                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error(
                            '[addAssignment] API call failed:',
                            errorText
                        );
                        // Revert the optimistic assignment.
                        setState(prev => {
                            const {[id]: _removed, ...rest} = prev.assignments;
                            return {...prev, assignments: rest};
                        });
                        // Self-heal: a failure here often means the client state
                        // referenced a stale id (e.g. a ghost team). Refetch the
                        // authoritative state so the dropdowns correct themselves
                        // without a manual page reload.
                        refetchState();
                        throw new Error(
                            `Failed to create assignment: ${errorText}`
                        );
                    }
                } catch (error) {
                    console.error('[addAssignment] API call error:', error);
                    // Revert (if not already reverted by the check above).
                    setState(prev => {
                        const {[id]: _removed, ...rest} = prev.assignments;
                        return {...prev, assignments: rest};
                    });
                    throw error;
                }
            }

            return id;
        },
        [
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl,
            refetchState
        ]
    );

    const updateAssignment = useCallback(
        (id: string, updates: Partial<ResourceProjectAssignment>) => {
            const state = stateRef.current;
            const previousAssignment = state.assignments[id];
            // Reclassifying Baseline <-> Additional Demand: the calc engine
            // reads each allocation's denormalised `type`, and the server
            // cascades it to child allocations — mirror that locally or the
            // numbers wouldn't move until a refetch.
            const newAllocationType = updates.allocationType;
            // Involvement is denormalised onto allocations the same way `type`
            // is — the server derives it from the assignment at read time, so
            // only this local cascade keeps loaded allocations fresh.
            const newInvolvement = updates.involvement;
            const previousAllocations =
                newAllocationType !== undefined || newInvolvement !== undefined
                    ? Object.fromEntries(
                          Object.entries(state.allocations).filter(
                              ([, alloc]) => alloc.assignmentId === id
                          )
                      )
                    : null;

            // Optimistic update
            setState(prev => {
                const next = {
                    ...prev,
                    assignments: {
                        ...prev.assignments,
                        [id]: {...prev.assignments[id], ...updates}
                    }
                };
                if (
                    newAllocationType !== undefined ||
                    newInvolvement !== undefined
                ) {
                    next.allocations = Object.fromEntries(
                        Object.entries(prev.allocations).map(
                            ([allocId, alloc]) => {
                                if (alloc.assignmentId !== id) {
                                    return [allocId, alloc];
                                }
                                const cascaded = {...alloc};
                                if (newAllocationType !== undefined) {
                                    cascaded.type = newAllocationType;
                                }
                                if (newInvolvement !== undefined) {
                                    cascaded.involvement = newInvolvement;
                                }
                                return [allocId, cascaded];
                            }
                        )
                    );
                }
                return next;
            });

            // The server treats null body fields as "not provided" (that's what
            // preserves fulfilment lineage on conversion), so an intentional
            // "no fulfilling role" is sent as an explicit clear flag.
            const body: Record<string, unknown> = {...updates};
            if ('jobRoleId' in updates && updates.jobRoleId === null) {
                body.clearJobRoleId = true;
            }

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/assignments/${id}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify(body)
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[updateAssignment] API call failed:',
                                await response.text()
                            );
                            // Revert on failure
                            setState(prev => ({
                                ...prev,
                                assignments: {
                                    ...prev.assignments,
                                    [id]: previousAssignment
                                },
                                allocations: previousAllocations
                                    ? {
                                          ...prev.allocations,
                                          ...previousAllocations
                                      }
                                    : prev.allocations
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[updateAssignment] API call error:',
                            error
                        );
                        // Revert on error
                        setState(prev => ({
                            ...prev,
                            assignments: {
                                ...prev.assignments,
                                [id]: previousAssignment
                            },
                            allocations: previousAllocations
                                ? {
                                      ...prev.allocations,
                                      ...previousAllocations
                                  }
                                : prev.allocations
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const deleteAssignment = useCallback(
        (id: string) => {
            const state = stateRef.current;
            // Store backup for revert
            const previousAssignment = state.assignments[id];
            const previousAllocations = Object.fromEntries(
                Object.entries(state.allocations).filter(
                    ([, alloc]) => alloc.assignmentId === id
                )
            );

            // Optimistic update
            setState(prev => {
                const newAssignments = {...prev.assignments};
                delete newAssignments[id];

                // Also delete all allocations for this assignment
                const newAllocations = Object.fromEntries(
                    Object.entries(prev.allocations).filter(
                        ([, alloc]) => alloc.assignmentId !== id
                    )
                );

                return {
                    ...prev,
                    assignments: newAssignments,
                    allocations: newAllocations
                };
            });

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/assignments/${id}`,
                    {
                        method: 'DELETE',
                        headers: {
                            'X-SignalR-Connection-Id': connectionId || ''
                        }
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[deleteAssignment] API call failed:',
                                await response.text()
                            );
                            setState(prev => ({
                                ...prev,
                                assignments: {
                                    ...prev.assignments,
                                    [id]: previousAssignment
                                },
                                allocations: {
                                    ...prev.allocations,
                                    ...previousAllocations
                                }
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[deleteAssignment] API call error:',
                            error
                        );
                        setState(prev => ({
                            ...prev,
                            assignments: {
                                ...prev.assignments,
                                [id]: previousAssignment
                            },
                            allocations: {
                                ...prev.allocations,
                                ...previousAllocations
                            }
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Allocation operations
    const addAllocation = useCallback(
        async (allocation: Omit<Allocation, 'id'>): Promise<string> => {
            const id = uuidv4();
            const newAllocation = {...allocation, id};

            // Optimistic update
            setState(prev => ({
                ...prev,
                allocations: {...prev.allocations, [id]: newAllocation}
            }));

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                try {
                    const response = await authenticatedFetch(
                        `${baseUrl}/api/resource-allocation/allocations`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-SignalR-Connection-Id': connectionId || ''
                            },
                            body: JSON.stringify({
                                id: id,
                                assignmentId: allocation.assignmentId,
                                projectId: allocation.projectId,
                                personId: allocation.personId,
                                teamId: allocation.teamId,
                                jobRoleId: allocation.jobRoleId,
                                globalWeekNumber: allocation.globalWeekNumber,
                                hours: allocation.hours,
                                notes: allocation.notes
                            })
                        }
                    );

                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error(
                            '[addAllocation] API call failed:',
                            errorText
                        );
                        setState(prev => {
                            const {[id]: _removed, ...rest} = prev.allocations;
                            return {...prev, allocations: rest};
                        });
                        throw new Error(
                            `Failed to create allocation: ${errorText}`
                        );
                    }
                } catch (error: unknown) {
                    console.error('[addAllocation] API call error:', error);
                    setState(prev => {
                        const {[id]: _removed, ...rest} = prev.allocations;
                        return {...prev, allocations: rest};
                    });
                    throw error;
                }
            }

            return id;
        },
        [
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const updateAllocation = useCallback(
        (id: string, updates: Partial<Allocation>) => {
            const previousAllocation = stateRef.current.allocations[id];

            // Optimistic update
            setState(prev => ({
                ...prev,
                allocations: {
                    ...prev.allocations,
                    [id]: {...prev.allocations[id], ...updates}
                }
            }));

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/allocations/${id}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify(updates)
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[updateAllocation] API call failed:',
                                await response.text()
                            );
                            setState(prev => ({
                                ...prev,
                                allocations: {
                                    ...prev.allocations,
                                    [id]: previousAllocation
                                }
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[updateAllocation] API call error:',
                            error
                        );
                        setState(prev => ({
                            ...prev,
                            allocations: {
                                ...prev.allocations,
                                [id]: previousAllocation
                            }
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const deleteAllocation = useCallback(
        (id: string) => {
            const previousAllocation = stateRef.current.allocations[id];

            // Optimistic update
            setState(prev => {
                const {[id]: _removed, ...rest} = prev.allocations;
                return {...prev, allocations: rest};
            });

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/allocations/${id}`,
                    {
                        method: 'DELETE',
                        headers: {
                            'X-SignalR-Connection-Id': connectionId || ''
                        }
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[deleteAllocation] API call failed:',
                                await response.text()
                            );
                            setState(prev => ({
                                ...prev,
                                allocations: {
                                    ...prev.allocations,
                                    [id]: previousAllocation
                                }
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[deleteAllocation] API call error:',
                            error
                        );
                        setState(prev => ({
                            ...prev,
                            allocations: {
                                ...prev.allocations,
                                [id]: previousAllocation
                            }
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Applies a whole multi-cell gesture (type-over selection, paste, move)
    // as ONE optimistic setState and ONE bulk request — the per-cell loop
    // recomputed the entire calc pipeline once per cell and broadcast one
    // tenant-wide SignalR event per cell.
    const applyAllocationEdits = useCallback(
        async (batch: AllocationEditBatch): Promise<void> => {
            const state = stateRef.current;
            const deleteIds = batch.deleteIds ?? [];
            if (batch.upserts.length + deleteIds.length === 0) return;

            // Materialise ids so the optimistic state and wire payload agree.
            const upserts: Allocation[] = batch.upserts.map(u => ({
                ...u,
                id: u.id ?? uuidv4()
            }));

            // Snapshot for revert.
            const previous: Record<string, Allocation | undefined> = {};
            for (const u of upserts) previous[u.id] = state.allocations[u.id];
            for (const id of deleteIds) previous[id] = state.allocations[id];

            // ONE optimistic update for the whole gesture.
            setState(prev => {
                const allocations = {...prev.allocations};
                for (const u of upserts) {
                    allocations[u.id] = allocations[u.id]
                        ? {...allocations[u.id], ...u}
                        : u;
                }
                for (const id of deleteIds) {
                    delete allocations[id];
                }
                return {...prev, allocations};
            });

            if (!customerId || !authenticatedFetch) return;

            const connectionId = signalRService.getConnectionId();
            try {
                const response = await authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/allocations/bulk`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify({
                            upserts: upserts.map(u => ({
                                id: u.id,
                                assignmentId: u.assignmentId,
                                globalWeekNumber: u.globalWeekNumber,
                                hours: u.hours,
                                notes: u.notes
                            })),
                            deleteIds
                        })
                    }
                );

                if (!response.ok) {
                    throw new Error(await response.text());
                }

                // Reconcile with the authoritative result (covers the rare
                // server-side slot-merge where ids differ from ours).
                const result: {
                    allocations: Allocation[];
                    deletedIds: string[];
                } = await response.json();
                setState(prev => {
                    const allocations = {...prev.allocations};
                    // Drop optimistic creates the server merged into another id.
                    const returnedIds = new Set(
                        result.allocations.map(a => a.id)
                    );
                    for (const u of upserts) {
                        if (!previous[u.id] && !returnedIds.has(u.id)) {
                            delete allocations[u.id];
                        }
                    }
                    for (const alloc of result.allocations) {
                        allocations[alloc.id] = allocations[alloc.id]
                            ? {...allocations[alloc.id], ...alloc}
                            : alloc;
                    }
                    for (const id of result.deletedIds) {
                        delete allocations[id];
                    }
                    return {...prev, allocations};
                });
            } catch (error) {
                console.error(
                    '[applyAllocationEdits] API call failed:',
                    error
                );
                // Revert the whole batch, then self-heal from the server.
                setState(prev => {
                    const allocations = {...prev.allocations};
                    for (const u of upserts) {
                        const prevAlloc = previous[u.id];
                        if (prevAlloc) {
                            allocations[u.id] = prevAlloc;
                        } else {
                            delete allocations[u.id];
                        }
                    }
                    for (const id of deleteIds) {
                        const prevAlloc = previous[id];
                        if (prevAlloc) {
                            allocations[id] = prevAlloc;
                        }
                    }
                    return {...prev, allocations};
                });
                refetchState();
                throw error;
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl,
            refetchState
        ]
    );

    return useMemo(
        () => ({
            addAssignment,
            updateAssignment,
            deleteAssignment,
            addAllocation,
            updateAllocation,
            deleteAllocation,
            applyAllocationEdits
        }),
        [
            addAssignment,
            updateAssignment,
            deleteAssignment,
            addAllocation,
            updateAllocation,
            deleteAllocation,
            applyAllocationEdits
        ]
    );
};
